import React, { useEffect, useState, useRef, useCallback, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Container,
    Paper,
    Typography,
    TextField,
    Grid,
    Chip,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Button,
    useTheme,
    useMediaQuery,
    alpha,
    Tooltip,
    TablePagination,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LanguageIcon from '@mui/icons-material/Language';
import CategoryIcon from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { schoolsService } from '../../../../services/api/schools';
import { School as SchoolType, SchoolsQueryParams } from '../../../../types/school';
import {
    Business,
    School as SchoolIcon,
    LocationCity,
    PhoneInTalk,
    EmailOutlined,
    CalendarMonth,
    EditOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';

// Animation keyframes
const floatAnimation = keyframes`
    0% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(5deg); }
    100% { transform: translateY(0px) rotate(0deg); }
`;

const shineAnimation = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const pulseAnimation = keyframes`
    0% { transform: scale(1); box-shadow: 0 0 0 0 ${alpha('#fff', 0.4)}; }
    70% { transform: scale(1.05); box-shadow: 0 0 0 10px ${alpha('#fff', 0)}; }
    100% { transform: scale(1); box-shadow: 0 0 0 0 ${alpha('#fff', 0)}; }
`;

const fadeInAnimation = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
}));

const FilterContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(145deg, 
        ${alpha(theme.palette.background.default, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    animation: 'slideIn 0.3s ease-out',
    '@keyframes slideIn': {
        '0%': {
            opacity: 0,
            transform: 'translateY(-20px)',
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, 
            ${alpha(theme.palette.primary.main, 0.3)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.3)} 100%)`,
        opacity: 0,
        transition: 'opacity 0.3s ease',
    },
    '&:hover::before': {
        opacity: 1,
    },
    '&:hover': {
        boxShadow: `0 6px 24px ${alpha(theme.palette.common.black, 0.12)}`,
    },
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        padding: theme.spacing(2),
    },
})) as typeof Box;

const SearchContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    position: 'relative',
})) as typeof Box;

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(8px)',
        paddingRight: theme.spacing(6), // Space for clear button
        '&:hover': {
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
            },
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
            '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
            },
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
            '&.Mui-focused': {
                color: theme.palette.primary.main,
            },
        },
        '& .MuiInputAdornment-root': {
            '& .MuiSvgIcon-root': {
                color: alpha(theme.palette.primary.main, 0.7),
                transition: 'all 0.3s ease',
            },
        },
        '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
            color: theme.palette.primary.main,
            transform: 'scale(1.1)',
        },
    },
})) as typeof TextField;

const ClearButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    padding: theme.spacing(0.5),
    color: alpha(theme.palette.text.secondary, 0.7),
    transition: 'all 0.2s ease',
    '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
})) as typeof IconButton;

const FilterChip = styled(Chip)(({ theme }) => ({
    marginTop: theme.spacing(1),
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    '& .MuiChip-deleteIcon': {
        color: alpha(theme.palette.primary.main, 0.7),
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
}));

const ActiveFiltersContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    minHeight: theme.spacing(4),
}));

const FilterLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& svg': {
        fontSize: '1rem',
        color: theme.palette.primary.main,
    },
})) as typeof Typography;

const SchoolsGrid = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
    },
}));

const SchoolCard = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'ref'
})<{ ref?: React.Ref<HTMLDivElement> }>(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    background: `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.85)} 0%, 
        ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
    borderRadius: theme.spacing(2.5),
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(12px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)},
                0 2px 8px ${alpha(theme.palette.primary.main, 0.05)},
                0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}`,
    animation: `${fadeInAnimation} 0.5s ease-out forwards`,
    opacity: 0,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.08)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
        opacity: 0.5,
        transition: 'opacity 0.3s ease',
    },
    '&:hover': {
        transform: 'translateY(-6px) scale(1.01)',
        boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)},
                    0 4px 12px ${alpha(theme.palette.primary.main, 0.08)},
                    0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
        background: `linear-gradient(165deg, 
            ${alpha(theme.palette.background.default, 0.9)} 0%, 
            ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
        '&::before': {
            opacity: 0.8,
        },
        '& .info-section': {
            background: `linear-gradient(165deg, 
                ${alpha(theme.palette.background.default, 0.85)} 0%, 
                ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
            boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}`,
            transform: 'translateY(-2px)',
        },
    },
}));

const CardHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3.5),
    position: 'relative',
    transition: 'all 0.3s ease',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    background: `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.85)} 0%, 
        ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, 
            ${alpha(theme.palette.primary.main, 0.4)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.4)} 100%)`,
        opacity: 0.95,
        transition: 'opacity 0.3s ease',
    },
    '& .card-header-gradient': {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.12)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.12)} 100%)`,
        opacity: 0.5,
        transition: 'opacity 0.3s ease',
    },
    '&:hover .card-header-gradient': {
        opacity: 0.8,
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2.5),
    },
}));

const CardContentWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
    overflow: 'hidden',
}));

const InfoSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(165deg, 
        ${alpha(theme.palette.background.default, 0.75)} 0%, 
        ${alpha(theme.palette.background.paper, 0.82)} 100%)`,
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.04)}`,
    transition: 'all 0.3s ease',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        opacity: 0.5,
        borderRadius: theme.spacing(2),
        transition: 'opacity 0.3s ease',
    },
    '&:hover': {
        background: `linear-gradient(165deg, 
            ${alpha(theme.palette.background.default, 0.8)} 0%, 
            ${alpha(theme.palette.background.paper, 0.88)} 100%)`,
        boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.06)}`,
        '&::before': {
            opacity: 0.8,
        },
    },
}));

const InfoSectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& svg': {
        fontSize: '1.1rem',
        color: alpha(theme.palette.primary.main, 0.9),
        transition: 'all 0.3s ease',
    },
    '&:hover svg': {
        transform: 'scale(1.1)',
        color: theme.palette.primary.main,
    },
}));

const InfoGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1),
    '& > *': {
        minWidth: 0,
    },
}));

const InfoItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1.5),
    background: alpha(theme.palette.background.default, 0.4),
    transition: 'all 0.2s ease',
    '&:hover': {
        background: alpha(theme.palette.background.default, 0.7),
        transform: 'translateX(4px)',
        '& svg': {
            transform: 'scale(1.15)',
            color: theme.palette.primary.main,
        },
    },
    '& > *:last-child': {
        minWidth: 0,
        flex: 1,
    },
    '& svg': {
        color: alpha(theme.palette.primary.main, 0.85),
        transition: 'all 0.3s ease',
    },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const TagsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    maxHeight: '40px',
    overflow: 'hidden',
}));

const Tag = styled(Chip)(({ theme }) => ({
    height: '20px',
    fontSize: '0.6875rem',
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    '& .MuiChip-label': {
        padding: '0 6px',
    },
}));

const StatusChip = styled(Chip)<{ status: 'active' | 'inactive' }>(({ theme, status }) => ({
    backgroundColor: status === 'active' 
        ? alpha(theme.palette.success.main, 0.15)
        : alpha(theme.palette.error.main, 0.15),
    color: status === 'active' 
        ? theme.palette.success.dark
        : theme.palette.error.dark,
    fontWeight: 600,
    padding: theme.spacing(0.75, 2),
    borderRadius: theme.spacing(3),
    border: `1px solid ${status === 'active' 
        ? alpha(theme.palette.success.main, 0.4)
        : alpha(theme.palette.error.main, 0.4)}`,
    backdropFilter: 'blur(8px)',
    transition: 'all 0.3s ease',
    boxShadow: `0 2px 8px ${status === 'active' 
        ? alpha(theme.palette.success.main, 0.2)
        : alpha(theme.palette.error.main, 0.2)}`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 4px 12px ${status === 'active' 
            ? alpha(theme.palette.success.main, 0.3)
            : alpha(theme.palette.error.main, 0.3)}`,
    },
    '& .MuiChip-label': {
        padding: '0 8px',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.5, 1.5),
        '& .MuiChip-label': {
            fontSize: '0.75rem',
        },
    },
}));

const CardActionButtons = styled(Box)(({ theme }) => ({
    marginTop: 'auto',
    padding: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(1),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    background: `linear-gradient(145deg, 
        ${alpha(theme.palette.background.default, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: 'blur(8px)',
    boxShadow: `0 -4px 12px ${alpha(theme.palette.common.black, 0.05)}`,
}));

const ActionButton = styled(Button)(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1.25, 2),
    fontSize: '0.875rem',
    fontWeight: 600,
    borderRadius: theme.spacing(2),
    transition: 'all 0.3s ease',
    textTransform: 'none',
    '&.MuiButton-outlined': {
        borderColor: alpha(theme.palette.primary.main, 0.4),
        '&:hover': {
            borderColor: theme.palette.primary.main,
            background: alpha(theme.palette.primary.main, 0.08),
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
    },
    '&.MuiButton-contained': {
        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
        },
    },
    '& .MuiButton-label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));

// Add a new styled component for the loading spinner
const LoadingSpinner = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    gap: theme.spacing(2),
    background: `linear-gradient(145deg, 
        ${alpha(theme.palette.background.paper, 0.8)} 0%, 
        ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(2),
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
    backdropFilter: 'blur(8px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    '& .MuiCircularProgress-root': {
        color: theme.palette.primary.main,
    },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 200,
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        backgroundColor: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(8px)',
        '&:hover': {
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
            },
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
            '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
            },
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
            '&.Mui-focused': {
                color: theme.palette.primary.main,
            },
        },
        '& .MuiSelect-icon': {
            color: alpha(theme.palette.primary.main, 0.7),
            transition: 'all 0.3s ease',
        },
        '&:hover .MuiSelect-icon': {
            color: theme.palette.primary.main,
            transform: 'scale(1.1)',
        },
    },
    '& .MuiSelect-select': {
        padding: theme.spacing(1.5, 2),
    },
}));

// Add these styled components near the other styled components
const LoadingOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.background.paper, 0.7),
    backdropFilter: 'blur(4px)',
    zIndex: 1,
    borderRadius: theme.spacing(2),
    transition: 'opacity 0.2s ease-in-out',
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    background: `linear-gradient(145deg, 
        ${alpha(theme.palette.background.paper, 0.8)} 0%, 
        ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
    borderRadius: theme.spacing(2),
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

export const SchoolsScreen: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    
    // Update state management
    const [schools, setSchools] = useState<SchoolType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalSchools, setTotalSchools] = useState(0);
    const [page, setPage] = useState(0); // MUI TablePagination uses 0-based index
    const [rowsPerPage, setRowsPerPage] = useState(12);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<'active' | 'inactive' | ''>('');
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

    // Update fetchSchools function to handle loading state consistently
    const fetchSchools = async (params: SchoolsQueryParams) => {
        setLoading(true);
        setError(null);
        try {
            const response = await schoolsService.getSchools({
                ...params,
                page: (params.page || 0) + 1, // Convert to 1-based index for API
                per_page: params.per_page,
            });
            
            setSchools(response.data.data);
            setTotalSchools(response.data.total);
        } catch (err) {
            setError(t('schools.errors.fetchFailed'));
            console.error('Error fetching schools:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page when changing rows per page
    };

    // Update search handler to use consistent loading state
    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            setPage(0);
            fetchSchools({
                page: 0,
                per_page: rowsPerPage,
                search: value,
                status: status as 'active' | 'inactive',
            });
        }, 500);

        setSearchTimeout(timeout);
    }, [status, rowsPerPage]);

    // Update status change handler to use consistent loading state
    const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const newStatus = event.target.value as 'active' | 'inactive' | '';
        setStatus(newStatus);
        setPage(0);
        fetchSchools({
            page: 0,
            per_page: rowsPerPage,
            search,
            status: newStatus as 'active' | 'inactive',
        });
    };

    // Effect for page/rowsPerPage changes
    useEffect(() => {
        fetchSchools({
            page,
            per_page: rowsPerPage,
            search,
            status: status as 'active' | 'inactive',
        });
    }, [page, rowsPerPage]);

    return (
        <Container maxWidth="lg" sx={{ 
            py: 5,
            background: `linear-gradient(165deg, 
                ${alpha(theme.palette.background.default, 0.92)} 0%, 
                ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
            minHeight: '100vh',
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '100%',
                background: `radial-gradient(circle at top right, 
                    ${alpha(theme.palette.primary.main, 0.05)} 0%, 
                    transparent 60%)`,
                pointerEvents: 'none',
            },
        }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{
                    fontWeight: 700,
                    background: `linear-gradient(90deg, 
                        ${theme.palette.primary.main} 0%, 
                        ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4,
                }}
            >
                {t('schools.title')}
            </Typography>

            <FilterContainer>
                <SearchContainer>
                    <FilterLabel>
                        <SearchIcon /> {t('schools.search')}
                    </FilterLabel>
                    <StyledTextField
                        fullWidth
                        placeholder={t('schools.search')}
                        value={search}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: loading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ) : search ? (
                                <ClearButton
                                    onClick={() => {
                                        setSearch('');
                                        setPage(0);
                                        fetchSchools({
                                            page: 0,
                                            per_page: rowsPerPage,
                                            status: status as 'active' | 'inactive',
                                        });
                                    }}
                                    size="small"
                                    aria-label="clear search"
                                >
                                    <CloseIcon fontSize="small" />
                                </ClearButton>
                            ) : null,
                        }}
                        inputProps={{
                            'aria-label': t('schools.search')
                        }}
                    />
                </SearchContainer>

                <Box>
                    <FilterLabel>
                        <FilterListIcon /> {t('schools.status.label')}
                    </FilterLabel>
                    <StyledFormControl>
                        <Select
                            value={status}
                            onChange={handleStatusChange}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>{t('common.all')}</em>
                            </MenuItem>
                            <MenuItem value="active">{t('schools.status.active')}</MenuItem>
                            <MenuItem value="inactive">{t('schools.status.inactive')}</MenuItem>
                        </Select>
                    </StyledFormControl>
                </Box>

                <ActiveFiltersContainer>
                    {search && (
                        <FilterChip
                            label={`${t('schools.search')}: ${search}`}
                            onDelete={() => {
                                setSearch('');
                                setPage(0);
                                fetchSchools({
                                    page: 0,
                                    per_page: rowsPerPage,
                                    status: status as 'active' | 'inactive',
                                });
                            }}
                            size="small"
                        />
                    )}
                    {status && (
                        <FilterChip
                            label={`${t('schools.status.label')}: ${t(`schools.status.${status}`)}`}
                            onDelete={() => {
                                setStatus('');
                                setPage(0);
                                fetchSchools({
                                    page: 0,
                                    per_page: rowsPerPage,
                                    search,
                                });
                            }}
                            size="small"
                        />
                    )}
                </ActiveFiltersContainer>
            </FilterContainer>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ position: 'relative' }}>
                {loading && !schools.length ? (
                    <LoadingContainer>
                        <CircularProgress size={40} />
                        <Typography variant="h6" color="textSecondary">
                            {t('schools.loading')}
                        </Typography>
                    </LoadingContainer>
                ) : schools.length === 0 ? (
                    <Box textAlign="center" py={4}>
                        <Typography color="textSecondary">
                            {t('schools.noSchools')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <SchoolsGrid 
                            container 
                            spacing={3}
                            sx={{
                                opacity: loading ? 0.7 : 1,
                                transition: 'opacity 0.2s ease-in-out',
                                position: 'relative',
                            }}
                        >
                            {schools.map((school, index) => (
                                <Grid item xs={12} sm={6} md={4} key={school.id}>
                                    <SchoolCard 
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                    >
                                        <CardHeader>
                                            <div className="card-header-gradient" />
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                <Tooltip title={school.name} placement="top">
                                                    <Typography 
                                                        variant="h6" 
                                                        component="h2" 
                                                        sx={{ 
                                                            fontWeight: 600,
                                                            color: theme.palette.text.primary,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            position: 'relative',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2em' }} />
                                                        {school.name}
                                                    </Typography>
                                                </Tooltip>
                                                <StatusChip
                                                    label={t(`schools.status.${school.status}`)}
                                                    status={school.status}
                                                    size="small"
                                                />
                                            </Box>
                                        </CardHeader>
                                        <CardContentWrapper>
                                            <InfoSection className="info-section">
                                                <InfoSectionTitle>
                                                    <Business /> {t('schools.details.contactInfo')}
                                                </InfoSectionTitle>
                                                <InfoGrid>
                                                    <InfoItem>
                                                        <EmailOutlined fontSize="small" />
                                                        <Box>
                                                            <InfoLabel>{t('schools.email')}</InfoLabel>
                                                            <InfoValue>{school.email}</InfoValue>
                                                        </Box>
                                                    </InfoItem>
                                                    <InfoItem>
                                                        <PhoneInTalk fontSize="small" />
                                                        <Box>
                                                            <InfoLabel>{t('schools.phone')}</InfoLabel>
                                                            <InfoValue>{school.phone}</InfoValue>
                                                        </Box>
                                                    </InfoItem>
                                                </InfoGrid>
                                            </InfoSection>

                                            <InfoSection className="info-section">
                                                <InfoSectionTitle>
                                                    <LocationCity /> {t('schools.details.location')}
                                                </InfoSectionTitle>
                                                <InfoGrid>
                                                    <InfoItem>
                                                        <LocationOnIcon fontSize="small" />
                                                        <Box>
                                                            <InfoLabel>{t('schools.address')}</InfoLabel>
                                                            <InfoValue>{school.address}</InfoValue>
                                                        </Box>
                                                    </InfoItem>
                                                </InfoGrid>
                                            </InfoSection>

                                            <InfoSection className="info-section">
                                                <InfoSectionTitle>
                                                    <CalendarMonth /> {t('schools.details.established')}
                                                </InfoSectionTitle>
                                                <InfoGrid>
                                                    <InfoItem>
                                                        <CalendarTodayIcon fontSize="small" />
                                                        <Box>
                                                            <InfoLabel>{t('schools.details.established')}</InfoLabel>
                                                            <InfoValue>{school.established}</InfoValue>
                                                        </Box>
                                                    </InfoItem>
                                                </InfoGrid>
                                            </InfoSection>
                                        </CardContentWrapper>

                                        <CardActionButtons>
                                            <ActionButton
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<VisibilityOutlined />}
                                                className="action-button"
                                            >
                                                {t('common.viewDetails')}
                                            </ActionButton>
                                            <ActionButton
                                                variant="contained"
                                                color="primary"
                                                startIcon={<EditOutlined />}
                                                className="action-button"
                                            >
                                                {t('common.edit')}
                                            </ActionButton>
                                        </CardActionButtons>
                                    </SchoolCard>
                                </Grid>
                            ))}
                        </SchoolsGrid>

                        {loading && (
                            <LoadingOverlay>
                                <CircularProgress size={40} />
                            </LoadingOverlay>
                        )}

                        {/* Pagination */}
                        <Box 
                            sx={{ 
                                mt: 3,
                                display: 'flex',
                                justifyContent: 'center',
                                background: alpha(theme.palette.background.paper, 0.8),
                                borderRadius: theme.spacing(1),
                                backdropFilter: 'blur(8px)',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            }}
                        >
                            <TablePagination
                                component="div"
                                count={totalSchools}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[12, 24, 36, 48]}
                                labelRowsPerPage={t('common.rowsPerPage')}
                                labelDisplayedRows={({ from, to, count }) => 
                                    `${from}-${to} ${t('common.of')} ${count}`
                                }
                                sx={{
                                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                        my: 0,
                                    },
                                }}
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Container>
    );
}; 